import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "./theme";
import {
  AlertTriangle, Clock, Wifi, ClipboardList, Utensils, Sparkles,
  Stethoscope, Heart, Building2, Zap, MessageCircle, BarChart2, RefreshCw,
} from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";

const REC_ICONS = {
  "Tempo de espera":          Clock,
  "Comunicação da equipe":    Wifi,
  "Conforto/estrutura":       Building2,
  "Atendimento médico":       Stethoscope,
  "Processos administrativos":ClipboardList,
  "Alimentação":              Utensils,
  "Limpeza":                  Sparkles,
};
const ELOGIO_ICONS = {
  "Equipe médica":  Stethoscope,
  "Enfermagem":     Heart,
  "Acolhimento":    Heart,
  "Estrutura":      Building2,
  "Rapidez":        Zap,
  "Comunicação":    MessageCircle,
};

function getIcon(categoria, isElogio) {
  const map = isElogio ? ELOGIO_ICONS : REC_ICONS;
  return map[categoria] || (isElogio ? Sparkles : AlertTriangle);
}

function CategoryCard({ item, isElogio, onPDSA }) {
  const Icon = getIcon(item.categoria, isElogio);
  const accentColor = isElogio ? T.green : T.red;

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: T.iconBoxSize, height: T.iconBoxSize, borderRadius: T.iconBoxRadius,
          background: `${accentColor}15`, border: `1px solid ${accentColor}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accentColor, flexShrink: 0,
        }}>
          <Icon size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: T.textHi, fontFamily: T.fontTitle }}>{item.categoria}</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <Badge color={accentColor}>{item.count}</Badge>
          <Badge color={T.textMid}>{item.pct}%</Badge>
        </div>
      </div>

      {(item.exemplos || []).slice(0, 2).map((ex, i) => (
        <div key={i} style={{
          borderLeft: `2px solid ${accentColor}40`, paddingLeft: 12,
          fontSize: 12, color: T.textMid, fontStyle: "italic", lineHeight: 1.5,
          fontFamily: T.fontBody,
        }}>
          "{ex}"
        </div>
      ))}

      {isElogio && item.nomes && item.nomes.length > 0 && (
        <div style={{ fontSize: 12, color: T.textLow, fontFamily: T.fontBody }}>
          <span style={{ color: T.green, fontWeight: 600 }}>Colaboradores elogiados: </span>
          {item.nomes.join(", ")}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPDSA(item, isElogio)}
          style={{
            background: T.accentDim, border: `1px solid ${T.accent}40`,
            color: T.accent,
          }}
        >
          Criar PDSA
        </Button>
      </div>
    </Card>
  );
}

const PERIODOS = [
  { key: "hoje",     label: "Hoje" },
  { key: "semana",   label: "Semana" },
  { key: "mes",      label: "Mês" },
  { key: "semestre", label: "Semestre" },
  { key: "ano",      label: "Ano" },
  { key: "todos",    label: "Todos" },
];

export default function Rankings() {
  const [tab, setTab]           = useState("reclamacoes");
  const [periodo, setPeriodo]   = useState("mes");
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const navigate                = useNavigate();

  const load = async (p) => {
    setLoading(true); setError(null);
    try {
      const qs = p ? `?periodo=${p}` : "";
      const r = await fetch(`/api/rankings${qs}`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      setData(await r.json());
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(periodo); }, [periodo]);

  const handlePDSA = (item, isElogio) => {
    const titulo = isElogio
      ? `Manutenção de excelência: ${item.categoria}`
      : `Melhoria: ${item.categoria}`;
    const descricao = isElogio
      ? `Categoria com ${item.count} elogios (${item.pct}% dos feedbacks positivos). Exemplos: ${(item.exemplos||[]).slice(0,2).join("; ")}`
      : `Categoria com ${item.count} reclamações (${item.pct}% dos feedbacks negativos). Exemplos: ${(item.exemplos||[]).slice(0,2).join("; ")}`;
    navigate("/pdsa", { state: { cicloNovo: { titulo, descricao } } });
  };

  const items = tab === "reclamacoes" ? (data?.reclamacoes || []) : (data?.elogios || []);
  const sorted = [...items].sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <PageContainer style={{ color: T.textHi, fontFamily: T.fontBody }}>
      <PageHeader icon={BarChart2} title="Rankings Semânticos" description="Categorias de feedback agrupadas por IA — baseado em depoimentos reais">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: T.bgInput, borderRadius: T.radiusSm, padding: 4, border: `1px solid ${T.border}` }}>
            {PERIODOS.map(p => (
              <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
                padding: "6px 12px", borderRadius: T.radiusXs, fontSize: 12, cursor: "pointer",
                background: periodo === p.key ? T.bgCard : "transparent",
                border: "none",
                color: periodo === p.key ? T.textHi : T.textLow,
                fontWeight: periodo === p.key ? 700 : 500,
                boxShadow: periodo === p.key ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                transition: T.transitionFast, fontFamily: T.fontBody, outline: "none",
              }}>{p.label}</button>
            ))}
          </div>
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => load(periodo)}>
            Atualizar
          </Button>
        </div>
      </PageHeader>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, background: T.bgInput, borderRadius: T.radiusSm, padding: 4, width: "fit-content" }}>
        {[
          { key: "reclamacoes", label: "Reclamações", color: T.red },
          { key: "elogios",     label: "Elogios",     color: T.green },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: T.fontBody,
              background: tab === key ? `${color}18` : "transparent",
              border: tab === key ? `1px solid ${color}40` : "1px solid transparent",
              color: tab === key ? color : T.textLow,
              transition: T.transitionFast,
            }}
          >
            {label}
            {data && (
              <span style={{
                marginLeft: 6, fontSize: 11, padding: "1px 7px", borderRadius: T.radiusFull,
                background: `${color}15`, color: color,
              }}>
                {tab === "reclamacoes" && key === "reclamacoes" ? (data.reclamacoes?.length || 0) : ""}
                {tab === "elogios"     && key === "elogios"     ? (data.elogios?.length     || 0) : ""}
                {tab !== key ? (key === "reclamacoes" ? (data.reclamacoes?.length || 0) : (data.elogios?.length || 0)) : ""}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && (
        <PageLoading label="Analisando depoimentos com IA..." />
      )}

      {error && !loading && (
        <div style={{
          padding: 20, background: `${T.red}10`, border: `1px solid ${T.red}30`,
          borderRadius: T.radius, color: T.red, fontSize: 13, fontFamily: T.fontBody,
        }}>
          Erro ao carregar rankings: {error}. Verifique a chave da API nas configurações.
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: T.textLow, fontSize: 14, fontFamily: T.fontBody }}>
          Nenhum dado disponível. Sincronize os depoimentos primeiro.
        </div>
      )}

      {!loading && !error && sorted.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {sorted.map((item, i) => (
            <CategoryCard
              key={i}
              item={item}
              isElogio={tab === "elogios"}
              onPDSA={handlePDSA}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
